import React, { useCallback, useMemo, useRef } from 'react';

const M_MIN = 1;
const M_MAX = 1000;
const M_STEP = 50;
const M_RECOMMENDED = 300;

/** Valores válidos: 1 m e depois de 50 em 50 até 1000. */
const M_OPTIONS = [M_MIN, ...Array.from({ length: M_MAX / M_STEP }, (_, i) => (i + 1) * M_STEP)];

/** Labels numéricos (evita poluir a régua). */
const LABEL_M = new Set([1, 100, 200, 300, 400, 500, 600, 700, 800, 900, 1000]);

function scaleToPosition(value, min, max) {
  if (max <= min) return 0;
  return ((value - min) / (max - min)) * 100;
}

function nearestOption(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n)) return M_MIN;
  let best = M_OPTIONS[0];
  let bestDist = Math.abs(best - n);
  for (let i = 1; i < M_OPTIONS.length; i += 1) {
    const d = Math.abs(M_OPTIONS[i] - n);
    if (d < bestDist) {
      best = M_OPTIONS[i];
      bestDist = d;
    }
  }
  return best;
}

function normalizeMetros(raw) {
  const n = parseInt(String(raw ?? ''), 10);
  if (!Number.isFinite(n)) return M_MIN;
  return nearestOption(Math.max(M_MIN, Math.min(M_MAX, n)));
}

function valueFromRatio(ratio) {
  const clamped = Math.max(0, Math.min(1, ratio));
  const raw = M_MIN + clamped * (M_MAX - M_MIN);
  return nearestOption(raw);
}

function formatMetros(n) {
  return `${n} m`;
}

export default function GoRaioKmSlider({ value, onChange, disabled = false }) {
  const normalized = normalizeMetros(value);
  const trackRef = useRef(null);
  const thumbLeft = scaleToPosition(normalized, M_MIN, M_MAX);
  const recommendedLeft = scaleToPosition(M_RECOMMENDED, M_MIN, M_MAX);
  const optionIndex = useMemo(() => M_OPTIONS.indexOf(normalized), [normalized]);

  const pickFromClientX = useCallback(
    (clientX) => {
      if (disabled) return;
      const track = trackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      onChange(valueFromRatio((clientX - rect.left) / rect.width));
    },
    [disabled, onChange]
  );

  const handlePointerDown = (event) => {
    if (disabled) return;
    event.preventDefault();
    pickFromClientX(event.clientX);

    const onMove = (moveEvent) => pickFromClientX(moveEvent.clientX);
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    const idx = optionIndex < 0 ? 0 : optionIndex;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      event.preventDefault();
      onChange(M_OPTIONS[Math.min(idx + 1, M_OPTIONS.length - 1)]);
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      event.preventDefault();
      onChange(M_OPTIONS[Math.max(idx - 1, 0)]);
    }
  };

  return (
    <div className={`go-raio-km-scale${disabled ? ' go-raio-km-scale--disabled' : ''}`}>
      <div className="go-raio-km-scale-control">
        <div className="go-raio-km-scale-rail">
          <div
            className="go-raio-km-scale-recommended"
            style={{ left: `${recommendedLeft}%` }}
            aria-hidden="true"
          >
            <span className="go-raio-km-scale-recommended-label">Recomendado</span>
            <span className="go-raio-km-scale-recommended-mark" />
          </div>
          <div
            ref={trackRef}
            className="go-raio-km-scale-track"
            role="slider"
            aria-valuemin={M_MIN}
            aria-valuemax={M_MAX}
            aria-valuenow={normalized}
            aria-valuetext={`${normalized} metros`}
            aria-label="Distância mínima em metros"
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : 0}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
          >
            <div className="go-raio-km-scale-line" aria-hidden="true" />
            <div
              className="go-raio-km-scale-fill"
              style={{ width: `${thumbLeft}%` }}
              aria-hidden="true"
            />
            <div
              className="go-raio-km-scale-thumb"
              style={{ left: `${thumbLeft}%` }}
              aria-hidden="true"
            />
          </div>
          <div className="go-raio-km-scale-ticks" aria-hidden="true">
            {M_OPTIONS.map((m) => {
              const left = scaleToPosition(m, M_MIN, M_MAX);
              const edgeClass =
                m === M_MIN
                  ? ' go-raio-km-scale-tick--start'
                  : m === M_MAX
                    ? ' go-raio-km-scale-tick--end'
                    : '';
              const showLabel = LABEL_M.has(m);
              const isRecommended = m === M_RECOMMENDED;
              return (
                <span
                  key={m}
                  className={`go-raio-km-scale-tick${edgeClass}${showLabel ? '' : ' go-raio-km-scale-tick--dot'}${isRecommended ? ' go-raio-km-scale-tick--recommended' : ''}`}
                  style={{ left: `${left}%` }}
                >
                  {showLabel ? m : ''}
                </span>
              );
            })}
          </div>
        </div>
        <span className="go-raio-km-scale-value">{formatMetros(normalized)}</span>
      </div>
    </div>
  );
}

export { M_MIN, M_MAX, M_STEP, M_RECOMMENDED, normalizeMetros };
